import React, { useEffect } from 'react';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useFormValidation } from '../../../hooks';
import { useRememberMe } from '../../../hooks/useRememberMe';
import { useLoadingStates } from '../../../hooks/useLoadingStates';
import { AuthMode } from './AuthModal';
import EmailInput from '../EmailInput';
import PasswordInput from '../PasswordInput';
import RememberMe from '../RememberMe';
import LoadingButton from '../LoadingButton';
import styles from './AuthModal.module.css';

interface LoginContentProps {
  onSwitchMode: (mode: AuthMode) => void;
  onSuccess: () => void;
  contextMessage?: string;
}

interface FormValues {
  email: string;
  password: string;
}

// Constants
const MIN_PASSWORD_LENGTH = 6;

const LoginContent: React.FC<LoginContentProps> = ({ onSwitchMode, onSuccess, contextMessage }) => {
  // Remember me functionality
  const {
    isRemembered,
    savedEmail,
    isStorageAvailable,
    saveRememberMe,
    clearRememberMe
  } = useRememberMe();

  // Loading states
  const { withLoading, isLoading } = useLoadingStates();
  const isGoogleLoading = isLoading('google');
  const isSignInLoading = isLoading('signin');

  // Form validation
  const initialValues: FormValues = { email: savedEmail || '', password: '' };
  const formConfig = {
    email: {
      required: true,
      rules: [
        {
          test: (value: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value),
          message: 'Please enter a valid email address'
        }
      ]
    },
    password: {
      required: true,
      rules: [
        {
          test: (value: string) => value.length >= MIN_PASSWORD_LENGTH,
          message: 'Password must be at least 6 characters'
        }
      ]
    }
  };

  const {
    values,
    errors,
    handleChange,
    handleBlur,
    setFieldError,
    isValid
  } = useFormValidation(initialValues, formConfig);

  const { email, password } = values;

  // Initialize form with saved email if available
  useEffect(() => {
    if (savedEmail && savedEmail !== email) {
      handleChange('email', savedEmail);
    }
  }, [savedEmail, email, handleChange]);

  // Check for redirect results on component mount
  useEffect(() => {
    const checkRedirect = async () => {
      // Dynamic import - keeps auth-providers out of initial bundle
      const { handleAuthRedirect } = await import('@/firebase/auth-providers');
      const result = await handleAuthRedirect();
      if (result?.user) {
        onSuccess();
      }
    };
    
    checkRedirect().catch((error) => {
      // Silently ignore redirect errors - they're normal when no redirect is pending
      if (error.code !== 'auth/argument-error' && error.code !== 'auth/invalid-argument') {
        console.error('Redirect check error:', error);
      }
    });
  }, [onSuccess]);

  // Handle remember me change
  const handleRememberMeChange = (remember: boolean) => {
    if (remember && email) {
      saveRememberMe(email, true);
    } else if (!remember) {
      clearRememberMe();
    }
  };

  // Google Sign In
  const handleGoogleSignIn = async () => {
    try {
      await withLoading('google', async () => {
        // Dynamic import - lazy-loads resolver only when clicked
        // Initial bundle stays small, resolver loads on-demand
        const { signInWithGoogle } = await import('@/firebase/auth-providers');
        const result = await signInWithGoogle();
        
        if (result.user) {
          // Check if user already exists in either userProfiles OR expertApplications
          try {
            const { getFirestoreLite } = await import('@/firebase/firestore-lite');
            const { doc, getDoc, setDoc, serverTimestamp } = await import('firebase/firestore/lite');
            const firestore = getFirestoreLite();
            
            const userDocRef = doc(firestore, 'userProfiles', result.user.uid);
            const expertDocRef = doc(firestore, 'expertApplications', result.user.uid);
            
            const [userDoc, expertDoc] = await Promise.all([
              getDoc(userDocRef),
              getDoc(expertDocRef)
            ]);
            
            if (!userDoc.exists() && !expertDoc.exists()) {
              // Create complete user profile with 100 points for new users
              const profileData = {
                firstName: result.user.displayName?.split(' ')[0] || '',
                lastName: result.user.displayName?.split(' ')[1] || '',
                displayName: result.user.displayName || '',
                email: result.user.email || '',
                photoURL: result.user.photoURL || '',
                points: 100, // Initialize with 100 points for new Google users
                createdAt: serverTimestamp(),
                updatedAt: serverTimestamp()
              };
              await setDoc(userDocRef, profileData, { merge: true });
              
              // Create welcome message for new users
              try {
                const { createWelcomeMessage } = await import('../../../services/WelcomeMessageService');
                await createWelcomeMessage(result.user.uid);
              } catch (error) {
                console.error('Error creating welcome message:', error);
              }
            }
          } catch (error) {
            console.error('Error checking/creating user profile:', error);
          }
          
          onSuccess();
        }
      });
    } catch (error: any) {
      console.error('Google sign in error:', error);
      
      // User-friendly error messages
      let errorMessage = 'Google sign in failed. Please try again.';
      
      if (error.code === 'auth/popup-closed-by-user') {
        errorMessage = 'Sign in cancelled';
      } else if (error.code === 'auth/popup-blocked') {
        errorMessage = 'Popup blocked. Please allow popups and try again.';
      } else if (error.code === 'auth/unauthorized-domain') {
        errorMessage = 'This domain is not authorized for Google Sign-in.';
      }
      
      setFieldError('email', errorMessage);
    }
  };

  // Email/Password Sign In
  const handleEmailSignIn = async () => {
    if (!isValid) return;

    try {
      await withLoading('signin', async () => {
        // Import auth from our centralized auth-providers
        const { auth } = await import('@/firebase/auth-providers');
        await signInWithEmailAndPassword(auth, email, password);
        
        // Save remember me preference on successful login
        if (isRemembered && email) {
          saveRememberMe(email, true);
        }
        
        onSuccess();
      });
    } catch (error: any) {
      console.error('Email sign in error:', error);
      const errorMessage = error.code === 'auth/invalid-credential' 
        ? 'Invalid email or password'
        : 'Sign in failed. Please try again.';
      setFieldError('password', errorMessage);
    }
  };

  // Keyboard navigation
  const handleEmailKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      const passwordField = document.getElementById('password-input');
      if (passwordField) {
        passwordField.focus();
      }
    }
  };

  const handlePasswordKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      handleEmailSignIn();
    }
  };

  return (
    <div className={styles.formContainer}>
      <div className={styles.mainContent}>
        {/* Header */}
        <div className={styles.authHeader}>
          <h1 className={styles.authTitle}>Log in or sign up</h1>
          <p className={styles.authSubtitle}>
            {contextMessage || 'Welcome to TioAssist.io'}
          </p>
        </div>

      {/* Social Login - Exact Airbnb 4 Buttons */}
      <div className={styles.socialLogin}>
        <LoadingButton
          variant="social"
          onClick={handleGoogleSignIn}
          loading={isGoogleLoading}
          loadingText="Signing in..."
          className={styles.socialButton}
        >
          <span className={styles.icon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" width="18" height="18">
              <path fill="#4285f4" d="M386 400c45-42 65-112 53-179H260v74h102c-4 24-18 44-38 57z"></path>
              <path fill="#34a853" d="M90 341a192 192 0 0 0 296 59l-62-48c-53 35-141 22-171-60z"></path>
              <path fill="#fbbc02" d="M153 292c-8-25-8-48 0-73l-63-49c-23 46-30 111 0 171z"></path>
              <path fill="#ea4335" d="M153 219c22-69 116-109 179-50l55-54c-78-75-230-72-297 55z"></path>
            </svg>
          </span>
          Continue with Google
        </LoadingButton>

{/*
        <button 
          className={styles.socialButton}
          onClick={() => console.log('Apple login not implemented yet')}
          type="button"
        >
          <span className={styles.icon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18">
              <path fill="currentColor" d="M12.152 6.896c-.948 0-2.415-1.078-3.96-1.04-2.04.027-3.91 1.183-4.961 3.014-2.117 3.675-.546 9.103 1.519 12.09 1.013 1.454 2.208 3.09 3.792 3.039 1.52-.065 2.09-.987 3.935-.987 1.831 0 2.35.987 3.96.948 1.637-.026 2.676-1.48 3.676-2.948 1.156-1.688 1.636-3.325 1.662-3.415-.039-.013-3.182-1.221-3.22-4.857-.026-3.04 2.48-4.494 2.597-4.559-1.429-2.09-3.623-2.324-4.39-2.376-2-.156-3.675 1.09-4.61 1.09zM15.53 3.83c.843-1.012 1.4-2.427 1.245-3.83-1.207.052-2.662.805-3.532 1.818-.78.896-1.454 2.338-1.273 3.714 1.338.104 2.715-.688 3.559-1.701"/>
            </svg>
          </span>
          Continue with Apple
        </button>
*/}

{/*
        <button 
          className={styles.socialButton}
          onClick={() => console.log('Meta login not implemented yet')}
          type="button"
        >
          
          <span className={styles.icon}>
  <svg
    width="18"
    height="18"
    viewBox="0 0 290 191"
    xmlns="http://www.w3.org/2000/svg"
  >
    <defs>
      <linearGradient
        id="Grad_Logo1"
        x1="61"
        y1="117"
        x2="259"
        y2="127"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" style={{ stopColor: "#0064e1" }} />
        <stop offset="0.4" style={{ stopColor: "#0064e1" }} />
        <stop offset="0.83" style={{ stopColor: "#0073ee" }} />
        <stop offset="1" style={{ stopColor: "#0082fb" }} />
      </linearGradient>
      <linearGradient
        id="Grad_Logo2"
        x1="45"
        y1="139"
        x2="45"
        y2="66"
        gradientUnits="userSpaceOnUse"
      >
        <stop offset="0" style={{ stopColor: "#0082fb" }} />
        <stop offset="1" style={{ stopColor: "#0064e0" }} />
      </linearGradient>
    </defs>

    <path
      fill="#0081fb"
      d="m31.06,125.96c0,10.98 2.41,19.41 5.56,24.51 4.13,6.68 10.29,9.51 16.57,9.51 8.1,0 15.51-2.01 29.79-21.76 11.44-15.83 24.92-38.05 33.99-51.98l15.36-23.6c10.67-16.39 23.02-34.61 37.18-46.96 11.56-10.08 24.03-15.68 36.58-15.68 21.07,0 41.14,12.21 56.5,35.11 16.81,25.08 24.97,56.67 24.97,89.27 0,19.38-3.82,33.62-10.32,44.87-6.28,10.88-18.52,21.75-39.11,21.75l0-31.02c17.63,0 22.03-16.2 22.03-34.74 0-26.42-6.16-55.74-19.73-76.69-9.63-14.86-22.11-23.94-35.84-23.94-14.85,0-26.8,11.2-40.23,31.17-7.14,10.61-14.47,23.54-22.7,38.13l-9.06,16.05c-18.2,32.27-22.81,39.62-31.91,51.75-15.95,21.24-29.57,29.29-47.5,29.29-21.27,0-34.72-9.21-43.05-23.09-6.8-11.31-10.14-26.15-10.14-43.06z"
    />
    <path
      fill="url(#Grad_Logo1)"
      d="m24.49,37.3c14.24-21.95 34.79-37.3 58.36-37.3 13.65,0 27.22,4.04 41.39,15.61 15.5,12.65 32.02,33.48 52.63,67.81l7.39,12.32c17.84,29.72 27.99,45.01 33.93,52.22 7.64,9.26 12.99,12.02 19.94,12.02 17.63,0 22.03-16.2 22.03-34.74l27.4-.86c0,19.38-3.82,33.62-10.32,44.87-6.28,10.88-18.52,21.75-39.11,21.75-12.8,0-24.14-2.78-36.68-14.61-9.64-9.08-20.91-25.21-29.58-39.71l-25.79-43.08c-12.94-21.62-24.81-37.74-31.68-45.04-7.39-7.85-16.89-17.33-32.05-17.33-12.27,0-22.69,8.61-31.41,21.78z"
    />
    <path
      fill="url(#Grad_Logo2)"
      d="m82.35,31.23c-12.27,0-22.69,8.61-31.41,21.78-12.33,18.61-19.88,46.33-19.88,72.95 0,10.98 2.41,19.41 5.56,24.51l-26.48,17.44c-6.8-11.31-10.14-26.15-10.14-43.06 0-30.75 8.44-62.8 24.49-87.55 14.24-21.95 34.79-37.3 58.36-37.3z"
    />
    <path
      fill="#192830"
      d="m347.94,6.04h35.93l61.09,110.52 61.1-110.52h35.15v181.6h-29.31v-139.18l-53.58,96.38h-27.5l-53.57-96.38v139.18h-29.31z m285.11,67.71c-21.02,0-33.68,15.82-36.71,35.41h71.34c-1.47-20.18-13.11-35.41-34.63-35.41z m-65.77,46.57c0-41.22 26.64-71.22 66.28-71.22 38.99,0 62.27,29.62 62.27,73.42v8.05h-99.49c3.53,21.31 17.67,35.67 40.47,35.67 18.19,0 29.56-5.55 40.34-15.7l15.57,19.07c-14.67,13.49-33.33,21.27-56.95,21.27-42.91,0-68.49-31.29-68.49-70.56z m164.09-43.97h-26.98v-24h26.98v-39.69h28.28v39.69h40.99v24h-40.99v60.83c0,20.77 6.64,28.15 22.96,28.15 7.45,0 11.72-.64 18.03-1.69v23.74c-7.86,2.22-15.36,3.24-23.48,3.24-30.53,0-45.79-16.68-45.79-50.07z m188.35,23.34c-5.68-14.34-18.35-24.9-36.97-24.9-24.2,0-39.69,17.17-39.69,45.14 0,27.27 14.26,45.27 38.53,45.27 19.08,0 32.7-11.1 38.13-24.91z m28.28,87.95h-27.76v-18.94c-7.76,11.15-21.88,22.18-44.75,22.18-36.78,0-61.36-30.79-61.36-70.95 0-40.54 25.17-70.83 62.92-70.83 18.66,0 33.3,7.46 43.19,20.63v-17.38h27.76z"
    />
  </svg>
</span>
Continue with Meta

        </button>
*/}

        {/* Continue with Email - Airbnb Style */}
        <button 
          className={styles.socialButton}
          onClick={() => onSwitchMode('signupEmail')}
          type="button"
        >
          <span className={styles.icon}>
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/>
              <polyline points="22,6 12,13 2,6"/>
            </svg>
          </span>
          Continue with email
        </button>

      </div>

      {/* Divider */}
      <div className={styles.divider}>
        <span className={styles.dividerText}>or</span>
      </div>

      {/* Email/Password Form */}
      <form onSubmit={(e) => { e.preventDefault(); handleEmailSignIn(); }}>
        {/* Email Input */}
        <div className={styles.inputGroup}>
          <EmailInput
            id="email-input"
            placeholder="Email address"
            value={email}
            onChange={(e) => handleChange('email', e.target.value)}
            onBlur={() => handleBlur('email')}
            onKeyDown={handleEmailKeyDown}
            hasError={!!errors.email}
            className={styles.formInput}
            autoComplete="email"
          />
          {errors.email && (
            <div className={styles.errorMessage}>
              {errors.email}
            </div>
          )}
        </div>

        {/* Password Input */}
        <div className={styles.inputGroup}>
          <PasswordInput
            id="password-input"
            placeholder="Password"
            value={password}
            onChange={(e) => handleChange('password', e.target.value)}
            onBlur={() => handleBlur('password')}
            onKeyDown={handlePasswordKeyDown}
            hasError={!!errors.password}
            className={styles.formInput}
            autoComplete="current-password"
          />
          {errors.password && (
            <div className={styles.errorMessage}>
              {errors.password}
            </div>
          )}
        </div>

        {/* Forgot Password */}
        <div style={{ textAlign: 'left', marginBottom: 'var(--auth-spacing-md)' }}>
          <span 
            onClick={() => onSwitchMode('password')}
            className={styles.forgotPasswordLink}
          >
            Forgot password?
          </span>
        </div>

        {/* Primary Action Button */}
        <LoadingButton
          variant="primary"
          onClick={handleEmailSignIn}
          loading={isSignInLoading}
          loadingText="Signing in..."
          className={styles.primaryButton}
          disabled={!isValid}
        >
          Continue
        </LoadingButton>
      </form>

        {/* Form Footer */}
        <div className={styles.formFooter}>
          <p className={styles.switchModeText}>
            Don't have an account?{' '}
            <span className={styles.switchModeLink} onClick={() => onSwitchMode('signup')}>
              Sign up
            </span>
          </p>
        </div>
      </div>

      {/* Terms - Pushed to Bottom */}
      <div className={styles.termsText}>
        By continuing, you agree to our{' '}
        <a href="/terms-of-service" target="_blank" rel="noopener">Terms of Service</a> and{' '}
        <a href="/privacy-policy" target="_blank" rel="noopener">Privacy Policy</a>.
      </div>
    </div>
  );
};

export default LoginContent;